import { Autocomplete, CircularProgress, TextField } from "@mui/material";

import { Device } from "@/shared/domain/devices";
import { useAllDevicesByOrg } from "@/shared/domain/devices/hooks/useAllDevicesByOrg";

import styles from "./DeviceSelector.module.css";

interface DeviceSelectorProps {
  deviceId: string | null;
  setDeviceId: (deviceId: string | null) => void;
  orgId: string | undefined;
  disabled?: boolean;
  size?: "small" | "medium";
  placeholder?: string;
}

export function DeviceSelector({
  deviceId,
  setDeviceId,
  orgId,
  disabled = false,
  size = "small",
  placeholder = "Select device",
}: DeviceSelectorProps) {
  const { data: devices, isLoading } = useAllDevicesByOrg(orgId);

  const options = devices ?? [];

  return (
    <Autocomplete
      id="device-selector"
      options={options}
      fullWidth
      freeSolo
      value={deviceId}
      size={size}
      disabled={disabled}
      loading={isLoading}
      getOptionLabel={(option) => {
        if (typeof option === "string") {
          return option;
        }
        return option.device_id;
      }}
      isOptionEqualToValue={(option, value) => {
        if (typeof option === "string" && typeof value === "string") {
          return option === value;
        }
        if (typeof option === "object" && typeof value === "string") {
          return option.device_id === value;
        }
        if (typeof option === "object" && typeof value === "object") {
          return option.device_id === value.device_id;
        }
        return false;
      }}
      renderOption={(props, option: Device | string) => (
        <li className={styles.optionItem} {...props}>
          <span className={styles.optionText}>
            {typeof option === "string" ? option : option.device_id}
          </span>
        </li>
      )}
      onChange={(_event, newValue: Device | string | null) => {
        if (newValue === null) {
          setDeviceId(null);
        } else if (typeof newValue === "object") {
          setDeviceId(newValue.device_id);
        } else {
          setDeviceId(newValue);
        }
      }}
      onInputChange={(_event, newInputValue, reason) => {
        // Ensures typed/pasted values are registered
        if (reason === "input") {
          setDeviceId(newInputValue || null);
        }
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder={placeholder}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {isLoading ? (
                  <CircularProgress color="inherit" size={16} />
                ) : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
      componentsProps={{
        paper: {
          className: styles.dropdownPaper,
        },
      }}
      className={styles.autocomplete}
    />
  );
}
